import { defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";
import type { IDomainMenu } from "~/domain/menu";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import type { SalesChannel } from "~/domain/sales-channels";
import { useBugReporter, useDirectBackend } from "#imports";

// Define the state interface
type IState = {
  isLoaded: boolean;
  isLoading: boolean;
  menus: IDomainMenu[];
};

function createInitialState(): IState {
  return {
    isLoaded: false,
    isLoading: false,
    menus: [],
  };
}

export const useMenusStore = defineStore("menus", () => {
  // Create a reactive state object using the initial state
  const state = reactive<IState>(createInitialState());

  const { outletRepo } = useDirectBackend();
  const { report } = useBugReporter();

  // Helper function to reset the state to its initial values
  function reset() {
    Object.assign(state, createInitialState());
  }

  // Helper function to patch the state with a partial object
  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  const menuIds = computed(() => state.menus.map((menu) => menu.id));

  const hasOneMenu = computed(() => state.menus.length === 1);

  const firstMenu = computed(() => state.menus?.[0]);

  async function loadMenus(
    businessId: string,
    outletId: string,
    orderingMode: IDomainOrderingMode,
    salesChannel: SalesChannel,
  ) {
    patch({ isLoading: true });

    try {
      const response = await outletRepo.getMenus({
        businessId,
        outletId,
        orderingMode,
        salesChannel,
      });

      patch({
        isLoaded: true,
        isLoading: false,
        menus: response.data,
      });
    } catch (error) {
      report(error);

      patch({
        isLoaded: false,
        isLoading: false,
        menus: [],
      });
    }
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    reset,
    menuIds,
    hasOneMenu,
    firstMenu,
    loadMenus,
  };
});
